import { cn } from "@/lib/utils";
import { useUserCats } from "@/hooks/useUserCats";
import { useContracts } from "@/hooks/useContracts";
import { getOpenSeaItemUrl } from "@/lib/opensea";
import { Skeleton } from "@/components/ui/skeleton";
import { Wallet, ExternalLink } from "lucide-react";

interface MyCatsStripProps {
  selectedId?: number | null;
  onSelect?: (tokenId: number) => void;
}

export function MyCatsStrip({ selectedId, onSelect }: MyCatsStripProps) {
  const { connected, requestConnect } = useContracts();
  const { cats, isLoading } = useUserCats();

  if (!connected) {
    return (
      <section className="border-y border-border bg-card/30 py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <h2 className="font-pixel text-xs text-primary">MY CATS</h2>
            <button
              onClick={requestConnect}
              className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <Wallet size={14} />
              Connect
            </button>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Connect your wallet to view your cats.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="border-y border-border bg-card/30 py-6">
      <div className="container mx-auto px-4">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-pixel text-xs text-primary">MY CATS</h2>
          <span className="text-xs text-muted-foreground">{cats.length} owned</span>
        </div>

        {isLoading ? (
          <div className="flex gap-3 overflow-x-auto pb-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-24 shrink-0 rounded-xl" />
            ))}
          </div>
        ) : cats.length === 0 ? (
          <p className="text-sm text-muted-foreground">No cats found. Mint your first one above.</p>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-2">
            {cats.map(({ tokenId }) => (
              <button
                key={tokenId}
                onClick={() => onSelect?.(tokenId)}
                className={cn(
                  "group relative aspect-square w-20 shrink-0 overflow-hidden rounded-xl border transition-colors md:w-24",
                  selectedId === tokenId
                    ? "border-primary ring-2 ring-primary"
                    : "border-border hover:border-primary/50"
                )}
              >
                <img
                  src={`/images/nfts-thumb/${tokenId}.webp`}
                  alt={`Cat #${tokenId}`}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
                <a
                  href={getOpenSeaItemUrl(tokenId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity hover:bg-primary group-hover:opacity-100"
                  aria-label={`View Cat #${tokenId} on OpenSea`}
                >
                  <ExternalLink size={12} />
                </a>
                <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-mono text-white">
                  #{tokenId}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
